/**
 * Structure of the user manual: which section every instruction page belongs
 * to, and which pages are sub-pages of another. The page content itself is
 * markdown in alineconf (config/instructions/<lang>/<slug>.md) and the titles
 * are translations (common.instructions-menu.chapters.<slug>), so adding a
 * page means adding its file, its title and one entry here.
 */

export interface InstructionSection {
  id: string;
  /** Translation key of the section name: the main menu's own label where there is one. */
  labelKey: string;
}

export interface InstructionPage {
  slug: string;
  section: string;
  /** Slug of the page this one is a sub-page of. One level only. */
  parent?: string;
}

/**
 * Sections in main menu order. General instructions match no view, so they
 * come first, apart from the rest.
 */
export const INSTRUCTION_SECTIONS: InstructionSection[] = [
  { id: 'general', labelKey: 'common.instructions-menu.sections.general' },
  { id: 'home', labelKey: 'common.dashboard' },
  { id: 'customers', labelKey: 'common.modules.customers' },
  { id: 'schedule', labelKey: 'common.modules.schedule' },
  { id: 'appointment-period', labelKey: 'common.modules.appointment-period' },
  { id: 'week-plan', labelKey: 'common.modules.week-plan' },
  { id: 'invoices', labelKey: 'common.modules.invoices' },
  { id: 'clinic-management', labelKey: 'common.modules.clinic-management' },
  { id: 'resources', labelKey: 'common.modules.resources' },
  { id: 'dashboard', labelKey: 'common.modules.dashboard' },
  { id: 'settings', labelKey: 'common.modules.settings' }
];

export const GENERAL_SECTION_ID = 'general';

/** Pages in reading order; a sub-page follows its parent. */
export const INSTRUCTION_PAGES: InstructionPage[] = [
  { slug: 'getting-started', section: 'general' },
  { slug: 'login', section: 'general' },
  { slug: 'language', section: 'general' },
  { slug: 'support-chat', section: 'general' },
  { slug: 'browser-support', section: 'general' },
  { slug: 'dictation-tools', section: 'general' },
  { slug: 'security', section: 'general' },
  { slug: 'technical-information', section: 'general' },

  { slug: 'home', section: 'home' },

  { slug: 'customer', section: 'customers' },
  { slug: 'customer-basic-info', section: 'customers' },
  { slug: 'sec-code', section: 'customers', parent: 'customer-basic-info' },
  { slug: 'health-record', section: 'customers' },
  {
    slug: 'era-forms-certificates',
    section: 'customers',
    parent: 'health-record'
  },
  { slug: 'era-other-features', section: 'customers', parent: 'health-record' },
  {
    slug: 'era-patient-archive',
    section: 'customers',
    parent: 'health-record'
  },
  {
    slug: 'phrase-feature-user-instructions',
    section: 'customers',
    parent: 'health-record'
  },
  { slug: 'examinations', section: 'customers' },
  { slug: 'laboratory-results', section: 'customers' },
  { slug: 'prescriptions', section: 'customers' },
  { slug: 'treatments', section: 'customers' },
  { slug: 'pgt', section: 'customers' },
  { slug: 'ivf-laboratory', section: 'customers' },
  { slug: 'semen-laboratory', section: 'customers' },
  { slug: 'storage', section: 'customers' },
  { slug: 'donors', section: 'customers' },
  { slug: 'donated-embryos', section: 'customers' },

  { slug: 'appointments', section: 'schedule' },
  { slug: 'patient-booking', section: 'schedule', parent: 'appointments' },

  { slug: '4-week-plan', section: 'week-plan' },

  { slug: 'billing', section: 'invoices' },
  { slug: 'open-invoice', section: 'invoices', parent: 'billing' },
  { slug: 'kela-forms', section: 'invoices', parent: 'billing' },
  { slug: 'freezing-billing', section: 'invoices', parent: 'billing' },
  { slug: 'payroll', section: 'invoices' },

  { slug: 'clinic-management', section: 'clinic-management' },
  { slug: 'treatment-cycles-overview', section: 'clinic-management' },

  { slug: '6-resources', section: 'resources' },

  { slug: 'thl-data-transfer', section: 'dashboard' },
  {
    slug: 'thl-treatment-recording',
    section: 'dashboard',
    parent: 'thl-data-transfer'
  },
  {
    slug: 'thl-anamnesis-recording',
    section: 'dashboard',
    parent: 'thl-data-transfer'
  },
  {
    slug: 'thl-stimulation-recording',
    section: 'dashboard',
    parent: 'thl-data-transfer'
  },
  {
    slug: 'thl-procedure-recording',
    section: 'dashboard',
    parent: 'thl-data-transfer'
  },
  {
    slug: 'thl-embryo-cultivate-recording',
    section: 'dashboard',
    parent: 'thl-data-transfer'
  },
  {
    slug: 'thl-sperm-sample-recording',
    section: 'dashboard',
    parent: 'thl-data-transfer'
  },
  {
    slug: 'thl-result-recording',
    section: 'dashboard',
    parent: 'thl-data-transfer'
  },
  {
    slug: 'thl-treatment-interruption',
    section: 'dashboard',
    parent: 'thl-data-transfer'
  },
  { slug: 'treatment-variable-search', section: 'dashboard' },
  { slug: 'treatment-results-summary', section: 'dashboard' },
  { slug: 'lab-performance-metrics', section: 'dashboard' },
  { slug: 'embryo-parameter-outcomes', section: 'dashboard' },

  { slug: 'settings-organizations', section: 'settings' },
  { slug: 'settings-general', section: 'settings' },
  { slug: 'settings-privileges', section: 'settings' },
  { slug: 'settings-staff', section: 'settings' },
  { slug: 'settings-products', section: 'settings' },
  { slug: 'settings-drugs', section: 'settings' },
  { slug: 'settings-laboratory-tests', section: 'settings' },
  { slug: 'settings-batch-numbers', section: 'settings' },
  { slug: 'settings-contract-templates', section: 'settings' },
  { slug: 'settings-record-templates', section: 'settings' }
];

const PAGES_BY_SLUG = new Map(INSTRUCTION_PAGES.map((p) => [p.slug, p]));

export function findInstructionPage(slug: string): InstructionPage | undefined {
  return PAGES_BY_SLUG.get(slug);
}

export function instructionTitleKey(slug: string): string {
  return `common.instructions-menu.chapters.${slug}`;
}

export function findInstructionSection(
  id: string
): InstructionSection | undefined {
  return INSTRUCTION_SECTIONS.find((s) => s.id === id);
}

export function pagesInSection(sectionId: string): InstructionPage[] {
  return INSTRUCTION_PAGES.filter((p) => p.section === sectionId);
}

export function childPages(slug: string): InstructionPage[] {
  return INSTRUCTION_PAGES.filter((p) => p.parent === slug);
}

/** Sections that have pages; a menu section without a manual yet stays out of sight. */
export function sectionsWithPages(): InstructionSection[] {
  return INSTRUCTION_SECTIONS.filter((s) => pagesInSection(s.id).length);
}

/**
 * A section's pages as parent-with-sub-pages groups, split into two columns
 * of roughly equal length. A group is never split, so a sub-page always
 * stays under its parent.
 */
export function sectionColumns(sectionId: string): InstructionPage[][][] {
  const groups: InstructionPage[][] = [];
  for (const page of pagesInSection(sectionId)) {
    if (page.parent && groups.length) {
      groups[groups.length - 1].push(page);
    } else {
      groups.push([page]);
    }
  }
  const total = groups.reduce((sum, g) => sum + g.length, 0);
  const left: InstructionPage[][] = [];
  let leftCount = 0;
  let i = 0;
  // Fill the left column while it stays nearer the half than it would with
  // the next group added.
  while (
    i < groups.length &&
    Math.abs(total / 2 - leftCount - groups[i].length) <=
      Math.abs(total / 2 - leftCount)
  ) {
    left.push(groups[i]);
    leftCount += groups[i].length;
    i++;
  }
  if (!left.length && groups.length) {
    left.push(groups[i++]);
  }
  return [left, groups.slice(i)];
}

/**
 * Related pages for the side column: the parent, the sub-pages, the other
 * sub-pages of the same parent, and pages the content links to.
 */
export function relatedPages(slug: string, linkedSlugs: string[]): string[] {
  const page = findInstructionPage(slug);
  const related = [
    ...(page?.parent ? [page.parent] : []),
    ...childPages(slug).map((p) => p.slug),
    ...(page?.parent
      ? childPages(page.parent)
          .map((p) => p.slug)
          .filter((s) => s !== slug)
      : []),
    ...linkedSlugs
  ];
  return related.filter(
    (s, i) => s !== slug && related.indexOf(s) === i && PAGES_BY_SLUG.has(s)
  );
}
